/**
 * Inverse computation for the flat workflow (BPMN) collab ops — the engine
 * behind per-user collaborative undo, mirroring sd-invert's simulation design.
 *
 * `invertWfOps(docBefore, ops, makeOpId?)` returns the ops that undo `ops`:
 * applying `ops` to a doc deep-equal to `docBefore` and then applying the
 * returned list yields a doc deep-equal to `docBefore` again (the INVERT LAW).
 * Inverse GROUPS come back in REVERSE order of `ops` (last op inverted first);
 * one forward op may need several inverse ops (e.g. a cascaded remove restores
 * every element AND its order slot), and those stay in in-group order.
 *
 * Inverses are computed by SIMULATION: `docBefore` is cloned, and for each op
 * (in order) the inverse group is recorded against the CURRENT simulated state,
 * after which the op is applied via `applyWfOps`. An op the applier SKIPS
 * contributes no inverse — it changed nothing. A `wfElementRemove` that was an
 * ACCEPTED NO-OP (nothing existed) also contributes no inverse for the same
 * reason, even though it counts as applied. Neither input is mutated.
 *
 * Positional contracts:
 *  - Restores and move-backs are anchored via `afterSiblingId` (null = it was
 *    first), never by index: anchor-relative insertion is the collab contract.
 *  - A remove's restore set is ordered container-first (containment depth,
 *    then bucket index): a subprocess is upserted back before its children so
 *    the children's `PARENT_GONE` guard never fires; every restored element is
 *    followed by a `wfContainerSet` that pins its exact pre-remove slot.
 *  - A replace-upsert that changed `containerId` restores the old record AND
 *    the old order slot (the re-append alone would land at the tail).
 *  - A group op over a field that was ABSENT before (geometry group, endpoint,
 *    host) inverts to a whole-record `wfElementUpsert` of the pre-op element:
 *    the group vocabulary cannot express field removal, so the record restore
 *    is the exact inverse (same fallback the differ uses forward).
 */

import { invertValueRemove, invertValueSet, ValueInverse } from "../sd-patch/sd-invert";
import { cloneJsonValue, ICollabJsonObject, isJsonObject } from "../sd-patch/sd-json";
import { CollabPathSegment, parsePointer } from "../sd-patch/sd-path";
import { applyWfOps } from "./wf-apply";
import {
  containerChainDepth,
  containerIdOf,
  isNonEmptyString,
  IWfOrderAnchor,
  orderAnchorOf,
  orderIndexOf
} from "./wf-doc";
import { IWfApplyResult, IWfCollabOp, makeWfOpId } from "./wf-op-types";
import { IWfBounds, IWfDocument, IWfElement, IWfPoint } from "./wf-types";

/** An inverse op before it is granted a fresh opId (assigned only to KEPT inverses). */
type WfOpDraft = Omit<IWfCollabOp, "opId">;

/** Doc roots writable through wfDocPropSet/wfDocPropRemove (kept in sync with wf-apply). */
const WF_DOC_PROP_ROOTS: ReadonlySet<string> = new Set<string>([
  "name",
  "parameters",
  "happyPathDefinition",
  "docExtras"
]);

function cloneElement(element: IWfElement): IWfElement {
  return cloneJsonValue(element as unknown as ICollabJsonObject) as unknown as IWfElement;
}

/** Whole-record restore: upsert(old) — exact when containerId is unchanged by the forward op. */
function recordRestoreDraft(element: IWfElement): WfOpDraft {
  return { kind: "wfElementUpsert", elementId: element.id, element: cloneElement(element) };
}

/** Restore drafts for one element: the record plus its exact pre-op order slot. */
function restoreWithSlotDrafts(simulation: IWfDocument, element: IWfElement): WfOpDraft[] {
  const anchor: IWfOrderAnchor = orderAnchorOf(simulation, element.id);
  const containerSet: WfOpDraft = {
    kind: "wfContainerSet",
    elementId: element.id,
    containerId: containerIdOf(element)
  };
  if (anchor.afterSiblingId !== undefined) {
    containerSet.afterSiblingId = anchor.afterSiblingId;
  }
  return [recordRestoreDraft(element), containerSet];
}

function invertWfElementUpsert(simulation: IWfDocument, op: IWfCollabOp): WfOpDraft[] | null {
  const payload: IWfElement | undefined = op.element;
  if (payload === undefined || !isJsonObject(payload as unknown) || !isNonEmptyString(payload.id)) {
    return null;
  }
  const existing: IWfElement | undefined = simulation.elements[payload.id];
  if (existing === undefined) {
    return [{ kind: "wfElementRemove", elementId: payload.id }];
  }
  const drafts: WfOpDraft[] = [recordRestoreDraft(existing)];
  if (containerIdOf(existing) !== (typeof payload.containerId === "string" ? payload.containerId : "")) {
    // The forward replace moves the element to the new container's tail; the
    // inverse upsert moves it back but would also land at a tail — pin the slot.
    const anchor: IWfOrderAnchor = orderAnchorOf(simulation, existing.id);
    const containerSet: WfOpDraft = {
      kind: "wfContainerSet",
      elementId: existing.id,
      containerId: containerIdOf(existing)
    };
    if (anchor.afterSiblingId !== undefined) {
      containerSet.afterSiblingId = anchor.afterSiblingId;
    }
    drafts.push(containerSet);
  }
  return drafts;
}

function invertWfElementRemove(simulation: IWfDocument, op: IWfCollabOp): WfOpDraft[] | null {
  if (!isNonEmptyString(op.elementId)) {
    return null;
  }
  const removedIds: string[] = [];
  const seen: Set<string> = new Set<string>();
  const candidates: string[] = [op.elementId];
  if (Array.isArray(op.cascadedIds)) {
    for (const cascadedId of op.cascadedIds) {
      if (isNonEmptyString(cascadedId)) {
        candidates.push(cascadedId);
      }
    }
  }
  for (const id of candidates) {
    if (!seen.has(id) && simulation.elements[id] !== undefined) {
      seen.add(id);
      removedIds.push(id);
    }
  }
  if (removedIds.length === 0) {
    return null; // Accepted no-op: nothing was removed, nothing to undo.
  }
  // Containers before containees, then bucket order, then id for determinism.
  removedIds.sort((a: string, b: string): number => {
    const depthDelta: number = containerChainDepth(simulation, a) - containerChainDepth(simulation, b);
    if (depthDelta !== 0) {
      return depthDelta;
    }
    const indexDelta: number = orderIndexOf(simulation, a) - orderIndexOf(simulation, b);
    if (indexDelta !== 0) {
      return indexDelta;
    }
    if (a === b) {
      return 0;
    }
    return a < b ? -1 : 1;
  });
  const drafts: WfOpDraft[] = [];
  for (const id of removedIds) {
    drafts.push(...restoreWithSlotDrafts(simulation, simulation.elements[id]));
  }
  return drafts;
}

function invertWfGeometrySet(simulation: IWfDocument, op: IWfCollabOp): WfOpDraft[] | null {
  if (!isNonEmptyString(op.elementId)) {
    return null;
  }
  const element: IWfElement | undefined = simulation.elements[op.elementId];
  if (element === undefined) {
    return null;
  }
  const carriesBounds: boolean = op.bounds !== undefined;
  const carriesWaypoints: boolean = op.waypoints !== undefined;
  const carriesLabelBounds: boolean = op.labelBounds !== undefined;
  if (!carriesBounds && !carriesWaypoints && !carriesLabelBounds) {
    return null; // The applier refuses an empty geometry op (BAD_OP).
  }
  const boundsWasAbsent: boolean = carriesBounds && element.bounds === undefined;
  const waypointsWereAbsent: boolean = carriesWaypoints && element.waypoints === undefined;
  const labelBoundsWereAbsent: boolean = carriesLabelBounds && element.labelBounds === undefined;
  if (boundsWasAbsent || waypointsWereAbsent || labelBoundsWereAbsent) {
    return [recordRestoreDraft(element)];
  }
  const inverse: WfOpDraft = { kind: "wfGeometrySet", elementId: op.elementId };
  if (carriesBounds) {
    inverse.bounds = cloneJsonValue(element.bounds as unknown as ICollabJsonObject) as unknown as IWfBounds;
  }
  if (carriesWaypoints) {
    inverse.waypoints = cloneJsonValue(element.waypoints) as unknown as IWfPoint[];
  }
  if (carriesLabelBounds) {
    if (element.labelBounds === null) {
      inverse.labelBounds = null;
    } else {
      inverse.labelBounds = cloneJsonValue(element.labelBounds as unknown as ICollabJsonObject) as unknown as IWfBounds;
    }
  }
  return [inverse];
}

function invertWfFlowEndpointsSet(simulation: IWfDocument, op: IWfCollabOp): WfOpDraft[] | null {
  if (!isNonEmptyString(op.elementId)) {
    return null;
  }
  const element: IWfElement | undefined = simulation.elements[op.elementId];
  if (element === undefined) {
    return null;
  }
  if (element.sourceId === undefined || element.targetId === undefined) {
    return [recordRestoreDraft(element)];
  }
  return [
    { kind: "wfFlowEndpointsSet", elementId: op.elementId, sourceId: element.sourceId, targetId: element.targetId }
  ];
}

function invertWfAttachSet(simulation: IWfDocument, op: IWfCollabOp): WfOpDraft[] | null {
  if (!isNonEmptyString(op.elementId)) {
    return null;
  }
  const element: IWfElement | undefined = simulation.elements[op.elementId];
  if (element === undefined) {
    return null;
  }
  if (element.attachedToId === undefined) {
    return [recordRestoreDraft(element)];
  }
  return [{ kind: "wfAttachSet", elementId: op.elementId, hostId: element.attachedToId }];
}

function invertWfContainerSet(simulation: IWfDocument, op: IWfCollabOp): WfOpDraft[] | null {
  if (!isNonEmptyString(op.elementId)) {
    return null;
  }
  const element: IWfElement | undefined = simulation.elements[op.elementId];
  if (element === undefined) {
    return null;
  }
  const anchor: IWfOrderAnchor = orderAnchorOf(simulation, element.id);
  const draft: WfOpDraft = { kind: "wfContainerSet", elementId: element.id, containerId: containerIdOf(element) };
  if (anchor.afterSiblingId !== undefined) {
    draft.afterSiblingId = anchor.afterSiblingId;
  }
  return [draft];
}

function invertWfProp(simulation: IWfDocument, op: IWfCollabOp): WfOpDraft[] | null {
  if (!isNonEmptyString(op.elementId) || typeof op.path !== "string") {
    return null;
  }
  const segments: CollabPathSegment[] | null = parsePointer(op.path);
  if (segments === null || segments.length === 0) {
    return null;
  }
  const element: IWfElement | undefined = simulation.elements[op.elementId];
  if (element === undefined) {
    return null;
  }
  if (segments[0].kind === "plain" && segments[0].key === "name") {
    // "/name" routes to the TOP-LEVEL element.name (never semantics) — same
    // routing as wf-apply. Deeper "/name/…" pointers always skip (no inverse).
    if (segments.length !== 1) {
      return null;
    }
    if (element.name !== undefined) {
      return [{ kind: "wfPropSet", elementId: op.elementId, path: "/name", value: element.name }];
    }
    return [{ kind: "wfPropRemove", elementId: op.elementId, path: "/name" }];
  }
  const semanticsRoot: Record<string, unknown> = isJsonObject(element.semantics as unknown) ? element.semantics : {};
  const inverse: ValueInverse | null =
    op.kind === "wfPropSet"
      ? invertValueSet(semanticsRoot, segments, op.value)
      : invertValueRemove(semanticsRoot, segments);
  if (inverse === null) {
    return null;
  }
  if (inverse.kind === "set") {
    return [{ kind: "wfPropSet", elementId: op.elementId, path: inverse.path, value: inverse.value }];
  }
  return [{ kind: "wfPropRemove", elementId: op.elementId, path: inverse.path }];
}

function invertWfDocProp(simulation: IWfDocument, op: IWfCollabOp): WfOpDraft[] | null {
  if (typeof op.path !== "string") {
    return null;
  }
  const segments: CollabPathSegment[] | null = parsePointer(op.path);
  if (segments === null || segments.length === 0) {
    return null;
  }
  const first: CollabPathSegment = segments[0];
  if (first.kind === "plain" && first.key === "participantBounds") {
    // Whole-value root; removal is refused by the applier, so the only
    // invertible forward op is a set over an EXISTING value → set(old back).
    // A set when the doc had no participantBounds (unreachable through the
    // extractor — a doc with a participant always carries it) has no
    // expressible inverse and contributes none.
    if (op.kind !== "wfDocPropSet" || segments.length !== 1) {
      return null;
    }
    const previous: IWfBounds | undefined = simulation.participantBounds;
    if (previous === undefined) {
      return null;
    }
    return [
      {
        kind: "wfDocPropSet",
        path: "/participantBounds",
        value: cloneJsonValue(previous as unknown as ICollabJsonObject)
      }
    ];
  }
  if (first.kind !== "plain" || !WF_DOC_PROP_ROOTS.has(first.key)) {
    return null; // The applier refuses non-whitelisted roots (BAD_PATH).
  }
  const inverse: ValueInverse | null =
    op.kind === "wfDocPropSet"
      ? invertValueSet(simulation, segments, op.value)
      : invertValueRemove(simulation, segments);
  if (inverse === null) {
    return null;
  }
  if (inverse.kind === "set") {
    return [{ kind: "wfDocPropSet", path: inverse.path, value: inverse.value }];
  }
  return [{ kind: "wfDocPropRemove", path: inverse.path }];
}

function invertWfLaneDerived(simulation: IWfDocument, op: IWfCollabOp): WfOpDraft[] | null {
  if (!isNonEmptyString(op.elementId)) {
    return null;
  }
  const element: IWfElement | undefined = simulation.elements[op.elementId];
  if (element === undefined) {
    return null;
  }
  if (element.laneId === undefined) {
    // laneId was ABSENT (not null) before — only a record restore is exact.
    return [recordRestoreDraft(element)];
  }
  return [{ kind: "wfLaneDerived", elementId: op.elementId, laneId: element.laneId }];
}

function invertOneWfOp(simulation: IWfDocument, op: IWfCollabOp): WfOpDraft[] | null {
  switch (op.kind) {
    case "wfElementUpsert":
      return invertWfElementUpsert(simulation, op);
    case "wfElementRemove":
      return invertWfElementRemove(simulation, op);
    case "wfGeometrySet":
      return invertWfGeometrySet(simulation, op);
    case "wfFlowEndpointsSet":
      return invertWfFlowEndpointsSet(simulation, op);
    case "wfAttachSet":
      return invertWfAttachSet(simulation, op);
    case "wfContainerSet":
      return invertWfContainerSet(simulation, op);
    case "wfPropSet":
    case "wfPropRemove":
      return invertWfProp(simulation, op);
    case "wfDocPropSet":
    case "wfDocPropRemove":
      return invertWfDocProp(simulation, op);
    case "wfLaneDerived":
      return invertWfLaneDerived(simulation, op);
    default:
      return null;
  }
}

/**
 * Computes the inverse op list for `ops` against `docBefore` (the doc state
 * BEFORE `ops` were applied). See the module doc for the invert law and the
 * simulation strategy. Every returned op carries a fresh id from `makeOpId`
 * (assigned in returned order).
 */
export function invertWfOps(
  docBefore: IWfDocument,
  ops: IWfCollabOp[],
  makeOpId: () => string = makeWfOpId
): IWfCollabOp[] {
  const simulation: IWfDocument = cloneJsonValue(docBefore as unknown as ICollabJsonObject) as unknown as IWfDocument;
  const draftGroups: WfOpDraft[][] = [];
  for (const op of ops) {
    const drafts: WfOpDraft[] | null = invertOneWfOp(simulation, op);
    const result: IWfApplyResult = applyWfOps(simulation, [op]);
    if (result.appliedCount === 1 && drafts !== null) {
      draftGroups.push(drafts);
    }
  }
  draftGroups.reverse();
  const inverses: IWfCollabOp[] = [];
  for (const group of draftGroups) {
    for (const draft of group) {
      inverses.push({ opId: makeOpId(), ...draft });
    }
  }
  return inverses;
}
